import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { ReceivingLogApiService } from '../../../domain/receiving-log/api/receiving-log-api.service';
import { EntraService } from '../../../service/entra.service';
import { ToastService } from '../../../service/toast.service';
import { ReceivingLogDetails, ReceivingLogEditItem } from '../../../models/logs/ReceivingLogDetails';
import { UpdateReceivingLogResponse } from '../../../models/logs/UpdateReceivingLogResponse';
import { LogRowsEditResult } from '../../../models/logs/LogRowsEditResult';
import { ContainerRow } from '../../../domain/receiving-log/rules/container-items.rules';
import {
  ContainerPlan,
  addsToExistingType,
  containerRowsFromItems,
  planContainerChanges,
  planRenumbersLabels,
} from '../../../domain/receiving-log/rules/container-plan.rules';
import {
  LogEditRefusal,
  LogEditSavedView,
  canCloseAfterSave,
  classifySaveRefusal,
  interpretSaveResponse,
  partStatusFailures,
  quantityChangeNotice,
  renumberedLabelWarning,
  saveToastMessage,
  touchedItemRows,
} from '../../../domain/receiving-log/rules/log-edit-outcome.rules';
import {
  ArrayRenderState,
  arrayRenderState,
  editItemCaption,
  printWarningLevel,
  quantityPreview,
  voidAttributionLine,
} from '../../../domain/receiving-log/rules/receiving-log-edit.rules';
import {
  missingNoteClause,
  receiptRowsToNoteLines,
  splitPreservedNote,
} from '../../../domain/receiving-log/rules/log-edit-items-string.rules';
import {
  logContributionsByLine,
  seedDamageByLine,
  seedLineItemsFromPo,
  seedOffPoRows,
} from '../../../domain/receiving-log/rules/log-edit-line-items.rules';
import {
  ReceiptPlan,
  emptyReceiptPlan,
  planReceiptChanges,
} from '../../../domain/receiving-log/rules/log-edit-receipt-plan.rules';
import { logTypeFromJobType } from '../../../domain/receiving-log/rules/log-type.rules';
import { receivingNotice } from '../../../domain/receiving-log/rules/receiving-flow.rules';
import { LogType } from '../../../models/logs/LogType.enum';
import { FormattedPoValues } from '../../../models/poData/FormattedPoValues';
import {
  CheckableLineItem,
  LineDamage,
} from '../../../service/receiving-mobile/log-entry-draft.store';
import {
  LogEditWorkingState,
  buildLogEditRequest,
  emptyLogEditState,
  hasPendingChanges,
  missingClauseNeedsReview,
  previewQuantityAfterEdit,
  receiptQuantitiesChanged,
  validateLogEditState,
} from '../../../domain/receiving-log/rules/receiving-log-save.rules';

/**
 * receiving-log-edit.component.ts
 *
 * The handoff-18 log-edit screen, rendered as a modal NESTED INSIDE the log
 * table's Entry Details modal. It loads `GET /V1/receiving-log/{logId}/details`
 * (§1) and drives the edit itself: receipt-row edits, container voids/additions,
 * the recomposed `items` note, and the `PUT /V1/receiving-log/{logId}` save
 * (§2/§3/§5), then reads the outcome (§4/§6/§7).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT COMMITS ON ITS OWN — and what that obliges the parent to do
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This modal does NOT stage edits for the parent's "Update Entry" button. The
 * two write to different endpoints in different transactions, so combining them
 * means one can succeed while the other fails and the log ends half-edited,
 * which is the exact contradiction this feature exists to eliminate.
 *
 * Committing on its own has a consequence the parent must honour: once this save
 * lands, the row the table loaded is HISTORICAL. `updateLogRow()` over there
 * diffs its form against that row, so it would see a difference in
 * `quantity`/`items` and push the stale strings back, silently clobbering what
 * was just derived. {@link LogRowsEditResult} is emitted on close for exactly
 * that reason — see `onRowEditorClosed` in `receiving-log.component.ts`.
 *
 * Three other behaviours here are contract, not styling:
 *
 *   - a `409` RE-RENDERS from the wrapper's `detail` and makes the user redo the
 *     edit. It never replays the request — see {@link applyRefusal}.
 *   - `renumberedItems` non-empty is rendered as a BLOCK, never as a toast: it
 *     means stickers already on boxes in the warehouse are wrong (§6.2.3). This
 *     screen does not print — reprinting is a manual step through the log
 *     table's reprint action — but removing the button did not remove the
 *     obligation to name the labels that went wrong, and the `PUT` response is
 *     the only place those facts exist.
 *   - a stored `MISSING LINE ITEM(S) …` clause cannot be recomputed from
 *     anything on this screen, so when the receipt quantities move it becomes a
 *     REQUIRED decision rather than a number that quietly goes stale. See
 *     {@link missingReviewNeeded}.
 */

/**
 * Which half of the log a given instance of this modal edits.
 *
 * `quantity` is a projection of the CONTAINER rows; `items` is a projection of
 * the RECEIPT rows. A user opens this to change one or the other, never both, so
 * each save carries only its own section of the request.
 */
export type LogEditMode = 'quantity' | 'items';

/** The page's fetch/render state. */
type EditState = 'loading' | 'loaded' | 'notFound' | 'error';

/**
 * Where the save has got to.
 *
 * `conflict` is deliberately its own state rather than a flavour of `refused`,
 * because it is the one refusal that CHANGES THE SCREEN: the `409`'s `detail` is
 * re-rendered and the pending edit is gone, so the user is looking at fresh state
 * and has to redecide (§5.2). §5.3 says to expect it routinely, not as an edge
 * case. `refused` covers the 400 / 404 / 500 / network cases, where the log is
 * exactly as it was and the edit is still on screen.
 */
type SaveState = 'idle' | 'confirming' | 'saving' | 'saved' | 'refused' | 'conflict';

/*
 * There is deliberately no `ReceiptDraft` type here any more.
 *
 * It existed to hold the on-screen values of one RECEIPT ROW, separately from the
 * edit map, so that the map could be kept down to only what differed — because
 * binding inputs straight to an edit map is how a screen ends up sending every
 * field it rendered, including the two that do damage (`completesLine: false`
 * re-opens a PO line; `isDamaged: false` erases damage).
 *
 * The check-off removes the need for it rather than the risk: the screen now edits
 * PO LINES, and `planReceiptChanges` derives the sparse patches by diffing the
 * edited lines against the seeded ones. Nothing on screen is bound to a receipt
 * row's field at all, so there is nothing to accidentally echo.
 */

@Component({
  selector: 'app-receiving-log-edit',
  templateUrl: './receiving-log-edit.component.html',
  styleUrls: ['./receiving-log-edit.component.css'],
})
export class ReceivingLogEditComponent implements OnChanges, OnDestroy {

  // #region Inputs / outputs

  /** Whether the nested modal is showing. Opening it (re)loads the log. */
  @Input() isOpen: boolean = false;

  /** Which log to edit. Set by the parent from the row behind the Entry Details modal. */
  @Input() logId: number | null = null;

  /**
   * Which half of the log this modal edits.
   *
   * The two are separate on purpose: `quantity` is a projection of the CONTAINER
   * rows and `items` a projection of the RECEIPT rows, they are edited with
   * completely different controls, and a user opens this to do one or the other.
   * Keeping both in one component is only about the parts that are genuinely
   * shared — the read, the `expected` block, the save, and every refusal and
   * outcome path — which is where the safety properties live and where two
   * copies would be two chances to get a 409 or a renumber warning wrong.
   */
  @Input() mode: LogEditMode = 'quantity';

  /**
   * Fired when the modal closes. Carries what the save STORED when one landed,
   * and `null` when the user closed without saving.
   *
   * The parent must re-baseline from a non-null result before its own update can
   * be trusted — see the class doc comment.
   */
  @Output() closed = new EventEmitter<LogRowsEditResult | null>();

  // #endregion

  // #region Properties

  /** Current fetch/render state driving which template block shows. */
  public state: EditState = 'loading';

  /** The full response from the details GET — nothing here has been edited. */
  public details: ReceivingLogDetails | null = null;

  /** Quantity preview text — quantityDisplay, falling back to log.quantity (§1.2). */
  public quantityPreviewText: string = '';

  /**
   * The container chips' current counts — what the delivery should hold when the
   * user is done, seeded from the log's ACTIVE item rows.
   *
   * This is a TARGET, not the edit. Tapping a chip up or down changes it, and
   * {@link containerPlan} works out what that means for the request: an increase
   * mints new items, a decrease names specific existing ones to tombstone.
   */
  public containerTargetRows: ContainerRow[] = [];

  /**
   * What {@link containerTargetRows} implies: containers to add, and the item ids
   * to tombstone.
   *
   * The void half is CHOSEN by `container-plan.rules.ts` — the highest-numbered
   * containers of the type — and is not offered to the user to move: there is no
   * per-container picker on this screen, deliberately (see
   * {@link onContainerRowsChange}). What the save bar shows is the COUNT coming off,
   * not which ids.
   */
  public containerPlan: ContainerPlan = { added: [], proposedVoidItemIds: [] };

  /** The preset chips offered in the container editor, matching the mobile Details step. */
  public readonly containerTypes: string[] = [
    'Box', 'Pallet', 'Crate', 'Skid', 'Bundle',
    'Roll', 'Tube', 'Bucket', 'Drum', 'Bag', 'Unit',
  ];

  /** Active (non-voided) item rows, for the "what's on this log now" list. */
  public activeItems: ReceivingLogEditItem[] = [];

  /** Voided item rows, rendered separately and always greyed. */
  public voidedItems: ReceivingLogEditItem[] = [];

  /** Whether to render the receipts grid, an empty-state message, or an unknown-state warning. */
  public receiptsState: ArrayRenderState = 'confirmedEmpty';

  /** Whether any print history exists worth a pre-save warning. */
  public printWarning: 'none' | 'printed' = 'none';

  /**
   * Whether the print-history list is expanded. Collapsed by default: it is
   * reference, not the task, and the one line that changes a decision — that
   * labels are already out in the warehouse — is shown either way.
   */
  public showPrintHistory: boolean = false;

  /**
   * Whether the previously-voided list is expanded. Collapsed by default for the
   * same reason as the print history — §1.2 requires it be VISIBLE, not that it
   * be the loudest thing on the screen.
   */
  public showVoidedItems: boolean = false;

  /**
   * Whether the "this delivery completed the PO" tick's explanation is showing.
   *
   * The tick's own label cannot carry the distinction it turns on — one writes a
   * clause on THIS log's note, the status row is the whole PO's — so the sentence
   * that can is a toggle beside it rather than permanent text on every open.
   *
   * There was a second flag beside this one for the reopen control's explanation.
   * That control is now a two-sentence radio that states the choice in full, so
   * there is nothing left for a help toggle to add.
   */
  public showClaimHelp: boolean = false;

  /**
   * The pending edit. Only ever holds what the user CHANGED — its receipt half is
   * derived by `planReceiptChanges` from the check-off, never bound to directly.
   */
  public edit: LogEditWorkingState = emptyLogEditState();

  /**
   * What the current check-off implies for `receipts` — recomputed after every tap.
   *
   * The check-off rows themselves, their baseline, and where they were seeded from
   * live in the check-off region further down; three doc comments describing them
   * were orphaned here when the fields moved.
   */
  public receiptPlan: ReceiptPlan = emptyReceiptPlan();

  /** Damage already recorded on this log, keyed by `lineKey`. READ-ONLY — see `seedDamageByLine`. */
  public damageByLine: Record<string, LineDamage> = {};

  /**
   * Where {@link checkoffRows} came from.
   *
   * `po` — the PO's own lines; expected quantities are real.
   * `receiptsOnly` — rebuilt from this log's receipts because po-data was not
   *   available (no PO on the log, or the fetch failed). Every line the log
   *   touched is editable, but nothing can say what was ORDERED, so no shortfall
   *   can be computed and none is offered.
   * `pending` — the fetch is still out.
   */
  public lineItemsSource: 'pending' | 'po' | 'receiptsOnly' = 'pending';

  /** The log's delivery type, from po-data's job type. Decides whether canceled PO lines are receivable. */
  public logType: LogType | null = null;

  /** The PO as fetched for items mode, or null when there is none / the fetch failed. */
  public poValues: FormattedPoValues | null = null;

  /**
   * The stored `MISSING LINE ITEM(S) …` clause as it read on load — the "before"
   * half of the shortfall decision, and what the editable field is prefilled with.
   */
  public storedMissingClause: string = '';

  /**
   * True once the user has answered the shortfall prompt, by editing the text or
   * by confirming it still reads right.
   */
  public missingReviewDone: boolean = false;

  /**
   * The receipt quantities as they stood when the shortfall prompt was answered.
   *
   * The answer was about THOSE numbers. Change a quantity again afterwards and it
   * stops being an answer, so the prompt has to come back — a confirmation that
   * outlives the numbers it was about is the same silent staleness in a new
   * place. Deliberately fingerprints only the quantity-bearing parts of the edit,
   * so fixing a typo in a notes column does not re-ask.
   */
  private _reviewedQuantities: string | null = null;

  /** Blocking problems, from `validateLogEditState`. Non-empty means nothing is sent. */
  public validationProblems: string[] = [];

  /** True when there is anything worth sending. A no-op PUT is legal (§4.4) — just pointless. */
  public pendingChanges: boolean = false;

  /** Local preview of the quantity string this edit will produce. The STORED value comes back on the response (§6.1). */
  public quantityAfterEdit: string = '';

  /** Local preview of the recomposed `items` note, or null when this edit sends none. */
  public notePreview: string | null = null;

  /** True when this edit moves item rows, so labels already in the warehouse become wrong (§3.4). */
  public renumbersLabels: boolean = false;

  public saveState: SaveState = 'idle';

  /** The raw `200` body, kept for reference; the screen reads {@link savedView}. */
  public saveResponse: UpdateReceivingLogResponse | null = null;

  /** The interpreted `200` (§6) — what the screen actually renders after a save. */
  public savedView: LogEditSavedView | null = null;

  /**
   * The interpreted refusal (§4, §5.2). `kind` tells a `400` from a `404` from a
   * `409` from a `500`; `message` is the API's own words, which for a `400` name
   * the offending ids and for a `409` name which ids moved.
   */
  public refusal: LogEditRefusal | null = null;

  /**
   * The `409`'s message, held separately from {@link refusal} because re-rendering
   * the conflict's `detail` runs {@link applyDetails}, which resets the save state
   * (correctly — the pending edit is void). The message has to outlive that reset
   * or the user is silently looking at different data than they edited.
   */
  public conflictMessage: string | null = null;

  /**
   * The stored quantity as it read BEFORE this save, captured at send time so the
   * §6.1 before/after can be shown. The AFTER always comes from the response.
   */
  private _quantityBeforeSave: string = '';

  /** The §6.1 before/after statement, or null when the stored quantity did not move. */
  public quantityNotice: string | null = null;

  /** The §6.2.3 "labels already in the warehouse are wrong" statement. Rendered as a BLOCK. */
  public renumberWarning: string | null = null;

  /**
   * True once a save has landed: the rows on screen describe the log as it was
   * BEFORE the edit, so a second save built on them would assert a stale
   * `expected` and earn a `409` (§5). Cleared only by a reload.
   */
  public staleAfterSave: boolean = false;

  /**
   * What the most recent successful save STORED, handed to the parent on close.
   * Null until a save lands, and never built from a local preview.
   */
  private _savedResult: LogRowsEditResult | null = null;

  /**
   * Guards a double close. `close()` emits, the parent clears `isOpen`, the modal
   * dismisses, and `didDismiss` calls `close()` again — a second emit that would
   * hand the parent a `null` result right after the real one and look like "the
   * user closed without saving".
   */
  private _closing: boolean = false;

  private _loadedLogId: number | null = null;
  private _fetchSub: Subscription | null = null;
  private _saveSub: Subscription | null = null;
  private _poSub: Subscription | null = null;

  // #endregion

  // #region Lifecycle

  constructor(
    private _api: ReceivingLogApiService,
    private _entraService: EntraService,
    private _toastService: ToastService,
  ) {}

  /**
   * Loads when the modal opens, and reloads if the parent points it at a
   * different log while it is open.
   *
   * Deliberately reloads on every open rather than caching: `expected` (§5) is
   * only as good as the read it came from, and this modal is opened from a table
   * whose rows can be minutes old.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isOpen'] && !changes['logId']) { return; }
    if (!this.isOpen) { return; }
    this._closing = false;
    if (this.state === 'loaded' && this._loadedLogId === this.logId && !changes['logId']) { return; }
    this.loadDetails();
  }

  ngOnDestroy(): void {
    this._fetchSub?.unsubscribe();
    this._saveSub?.unsubscribe();
    this._poSub?.unsubscribe();
  }

  // #endregion

  // #region Methods

  /**
   * Closes the modal, handing back what the save stored (or null when nothing
   * was saved).
   *
   * Deliberately does NOT warn about unsaved edits — a save here is explicit and
   * multi-step, and the rows are reloaded from the server on every open, so
   * leaving discards nothing that was ever written.
   */
  public close(): void {
    if (this._closing) { return; }
    this._closing = true;
    const result = this._savedResult;
    this.resetForNextOpen();
    this.closed.emit(result);
  }

  /** Drops everything so a reopen starts from a fresh read rather than a stale screen. */
  private resetForNextOpen(): void {
    this._fetchSub?.unsubscribe();
    this._saveSub?.unsubscribe();
    this._poSub?.unsubscribe();
    this.state = 'loading';
    this.details = null;
    this._loadedLogId = null;
    this._savedResult = null;
    this.conflictMessage = null;
  }

  /**
   * Fetches the log details plus its (unchanged, existing-endpoint) photo
   * panels. Public so the error state's "Try again" button can re-run it.
   */
  public loadDetails(): void {
    this._fetchSub?.unsubscribe();

    if (this.logId == null || Number.isNaN(this.logId) || this.logId <= 0) {
      this.state = 'notFound';
      return;
    }

    this.state = 'loading';
    // A deliberate (re)load starts clean. applyDetails does NOT clear this, because
    // a 409 re-renders through it and the conflict message has to survive that.
    this.conflictMessage = null;
    const logId = this.logId;

    this._fetchSub = this._api.getReceivingLogDetails(logId).subscribe({
      next: (details) => {
        this.applyDetails(details);
        this._loadedLogId = logId;
        this.state = 'loaded';
      },
      error: (err) => {
        console.error('Error fetching receiving log details:', err);
        // Errors arrive normalized as { message, details, originalError }.
        const status = err?.originalError?.status;
        this.state = status === 404 ? 'notFound' : 'error';
      },
    });
  }

  /**
   * Drops the pending edit — ONE place, because every field it touches is a
   * separate copy of "what the user has changed and not yet sent", and forgetting
   * one is how both of the correctness bugs in the previous pass happened.
   *
   * ─────────────────────────────────────────────────────────────────────────────
   * WHY THERE ARE THREE COPIES TO CLEAR AND NOT ONE
   * ─────────────────────────────────────────────────────────────────────────────
   *
   * `edit` is what goes on the wire, but {@link containerPlan} and
   * {@link receiptPlan} are second copies of the same pending edit, each holding
   * facts `edit` cannot answer — `plan.unsupported`, and the
   * `proposedVoidItemIds` {@link voidRenumbersSurvivors} is computed from. So they
   * are neither derivable from `edit` nor safe to leave behind:
   *
   *   - Leaving `containerPlan` behind after a save was a real bug. Everything
   *     derived from it kept reading as though the save had not happened —
   *     `containersChanged` stayed true so the "new quantity field text" card
   *     stayed on screen, while `quantityAfterEdit`, which reads the now-empty
   *     `edit`, recomputed back to the PRE-edit string. The card showed the OLD
   *     quantity under a heading promising the new one, next to an outcome block
   *     correctly reporting the new one. `addsToExistingContainerType` was stale
   *     the same way, so the renumber warning also outlived its edit.
   *   - The PO-completion answer goes for the same reason as the shortfall answer:
   *     it is a statement about an edit that no longer exists, and its "another
   *     delivery completed it" arm writes nothing, so a stale one is invisible.
   *   - Leaving the shortfall answer behind was the other. All THREE of its fields
   *     have to go: on a log with no stored clause `_noMissingDeclared` is the only
   *     thing the gate consults, so a "nothing is still owed" pressed before a
   *     reset went on answering for whatever was edited afterwards, and the prompt
   *     this screen exists to force never appeared again.
   *
   * The answer is also cleared because it was an answer ABOUT quantities that no
   * longer exist — see {@link _reviewedQuantities}.
   *
   * Callers add only their own extra: re-seeding the chips, re-copying the
   * baseline rows, `staleAfterSave`, and so on.
   */
  private clearPendingEdit(): void {
    this.edit = emptyLogEditState();
    this.containerPlan = { added: [], proposedVoidItemIds: [] };
    this.receiptPlan = emptyReceiptPlan();
    this.missingReviewDone = false;
    this._reviewedQuantities = null;
    this._noMissingDeclared = false;
    this._poCompletionAnswered = false;
  }

  /**
   * Derives every rendering-only value the template needs from the raw
   * response, applying the handoff-18 §1.1/§1.2 rules via the pure rules
   * module rather than re-deciding them inline here.
   */
  private applyDetails(details: ReceivingLogDetails): void {
    this.details = details;
    this.quantityPreviewText = quantityPreview(details);

    // Voided rows are rendered, greyed, with attribution — never filtered out
    // (a user who can't see "BOX 3 was voided" will re-add it). They are
    // already excluded from containerItems/quantityDisplay server-side.
    this.activeItems = (details.items ?? []).filter((i) => !i.isVoided);
    this.voidedItems = (details.items ?? []).filter((i) => i.isVoided);

    // The chips start on what the log actually holds, so the first thing the user
    // sees is the delivery as it stands rather than an empty grid they have to
    // rebuild from memory.
    this.containerTargetRows = containerRowsFromItems(this.activeItems);

    // Check errorMessages before rendering "no receipts/items on this log" —
    // an empty array from a best-effort sub-fetch is ambiguous on its own.
    this.receiptsState = arrayRenderState(details.receipts, details.errorMessages);

    this.printWarning = printWarningLevel(details.printHistory);

    // A fresh load discards any pending edit — including after a 409, where the
    // user must REDO the edit rather than have it replayed against new state, and
    // where the whole point is that the shortfall is answered again against what is
    // actually there.
    //
    // The check-off is seeded separately, by loadLineItems, because it needs the PO
    // as well as the log. Until that lands the rows are empty and the plan is empty,
    // so nothing here can produce a receipt change.
    this.clearPendingEdit();
    this.checkoffRows = [];
    this._baselineRows = [];
    this.expandedRowIndices = [];
    this.damageByLine = {};
    this.lineItemsSource = 'pending';

    // The shortfall field is PREFILLED, never left blank: the stored clause is a
    // human's recorded judgment and the screen has no business dropping it. The
    // working state stays `null` (untouched) until the user actually types, so a
    // prefill is not mistaken for an edit.
    this.storedMissingClause = missingNoteClause(details.log?.items);
    // Both of these are facts about the response, not about the edit, so they are
    // read once here rather than re-derived on every change-detection pass. The PO
    // half is re-read by loadLineItems when its fetch lands; until then there is no
    // PO, which is what `false` says.
    this.storedLogCompletesPo = splitPreservedNote(details.log?.items).poComplete;
    // The PO read belongs to the load that fetched it: a 409 re-renders through here
    // and its PO is read again, so carrying the old one across would leave
    // `isLegacyPo` describing a PO nothing on screen came from.
    this.poValues = null;
    this.logType = null;
    this.refreshLegacyPo();

    this.saveState = 'idle';
    this.saveResponse = null;
    this.savedView = null;
    this.refusal = null;
    this.quantityNotice = null;
    this.renumberWarning = null;
    this.staleAfterSave = false;
    this.refreshDerived();

    if (this.mode === 'items') { this.loadLineItems(); }
  }

  /**
   * ITEMS MODE ONLY. Fetches the PO so the check-off can show what was ORDERED,
   * and seeds the rows either way.
   *
   * The details payload deliberately REPLACED the `po-data` call (§1), so it
   * carries no `quantityExpected` — which is fine for the container editor and not
   * for this one: without an expected quantity there is nothing for a received
   * amount to fall short of, so the MISSING clause stays a text field rather than
   * a derivation. One extra GET in items mode buys it back.
   *
   * `validate-po` is skipped on purpose. A log's PO cannot be changed after submit
   * (§3.5), so it was valid when the log was written; a PO since removed from the
   * ERP fails the fetch anyway and lands in the same degraded path.
   *
   * A failed fetch is NOT an error state. It seeds from the log's own receipt rows
   * instead and says so — a degraded check-off still lets the user fix what the
   * log says, while an error state would leave `items` locked with nowhere to go,
   * which is worse than the contradiction the lock exists to prevent.
   */
  public loadLineItems(): void {
    this._poSub?.unsubscribe();

    const details = this.details;
    if (!details) { return; }

    const poNumber = (details.log?.poNumber ?? '').trim();
    if (!poNumber) {
      // No PO at all: nothing to fetch, and the degraded path is the only path.
      this.seedFromReceiptsOnly();
      return;
    }

    this.lineItemsSource = 'pending';
    this._poSub = this._api.getPoData(poNumber).subscribe({
      next: (poData) => this.seedFromPo(new FormattedPoValues(poData)),
      error: (err) => {
        console.error('Could not read PO data for the items editor:', err);
        this.seedFromReceiptsOnly();
      },
    });
  }

  /** Seeds the check-off from the PO's own lines, with this log's receipts overlaid. */
  private seedFromPo(poValues: FormattedPoValues): void {
    const logReceipts = this.details?.receipts ?? [];

    this.poValues = poValues;
    this.refreshLegacyPo();
    // Project vs Service decides whether an ERP-canceled line is receivable. The
    // log row does not record it; the PO's job type is the only source.
    this.logType = logTypeFromJobType(poValues.jobInformation?.jobType ?? '');

    const seeded = seedLineItemsFromPo({
      lineItems: poValues.lineItems,
      logReceipts,
      isProject: this.logType === LogType.Project,
    });
    const seededOffPo = seedOffPoRows(logReceipts);

    this.damageByLine = seedDamageByLine(
      seeded,
      logContributionsByLine(logReceipts),
      seededOffPo,
      logReceipts,
    );
    this.adoptSeed(seeded, seededOffPo);
    this.lineItemsSource = 'po';
  }

  /**
   * Seeds the check-off from this log's receipt rows alone — the degraded path.
   *
   * `receiptRowsToNoteLines` is the existing inverter the note recomposition uses.
   * It stamps every row `isClosed: true` and leaves the expected quantities blank
   * ON PURPOSE, so no shortfall can be computed from data this screen does not
   * have. That is exactly the right shape here: the user can still correct what
   * arrived, and nothing fabricates a number about what is owed.
   */
  private seedFromReceiptsOnly(): void {
    const logReceipts = this.details?.receipts ?? [];
    const { lineItemRows, damageByLine } = receiptRowsToNoteLines(logReceipts);

    this.poValues = null;
    this.refreshLegacyPo();
    this.logType = null;
    this.damageByLine = damageByLine;
    this.adoptSeed(
      lineItemRows.filter((row) => !row.isOffPo),
      lineItemRows.filter((row) => !!row.isOffPo),
    );
    this.lineItemsSource = 'receiptsOnly';
  }

  /**
   * Takes a freshly seeded check-off as both the BASELINE and the editable copy.
   *
   * The two must not share row objects: the editable rows are mutated in place by
   * the check-off controls, and a shared reference would move the baseline along
   * with them, so every line would report itself unedited and the save would send
   * nothing.
   */
  private adoptSeed(lineRows: CheckableLineItem[], offPoRows: CheckableLineItem[]): void {
    // PO lines first, then off-PO — one array, because that is what the check-off
    // takes and what `planReceiptChanges` reconciles, and because the off-PO rows'
    // negative `line` numbering is only unique against the PO lines when both live
    // in the same set.
    const seeded = [...lineRows, ...offPoRows];

    // The baseline and the editable copy must not share row objects: the check-off
    // replaces rows as the user works, and a shared reference would drag the
    // baseline along, so every line would report itself unedited and the save would
    // send nothing at all.
    this._baselineRows = seeded;
    this.checkoffRows = seeded.map((row) => ({ ...row }));

    // Which lines this log actually put receipts on. Read from the PAYLOAD rather
    // than from the seeded rows' status, because the user can clear a status and
    // that must not turn an editable line read-only underneath them.
    this._linesWithLogReceipts = new Set(
      (this.details?.receipts ?? [])
        .map((receipt) => receipt.poLineSequence)
        .filter((sequence): sequence is number => sequence != null),
    );

    // Open the lines this delivery actually touched — those are why the user is
    // here. The submit starts everything closed because nothing has been acted on
    // yet; here, something has.
    this.expandedRowIndices = this.checkoffRows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row.isOffPo
        || (row.poLineSequence != null && this._linesWithLogReceipts.has(row.poLineSequence)))
      .map(({ index }) => index);

    this.applyLineItemPlan();
  }

  /**
   * Translates the current check-off into `receipts` changes and folds them into
   * the pending edit.
   *
   * REPLACES the three receipt fields rather than accumulating into them: the plan
   * is derived fresh from baseline-versus-edited every time, so it is already the
   * whole truth about what the check-off implies. Merging would leave behind
   * patches for a line the user has since put back.
   */
  private applyLineItemPlan(): void {
    if (this.mode !== 'items') { return; }

    this.receiptPlan = planReceiptChanges(
      this.details?.receipts ?? [],
      this._baselineRows,
      this.checkoffRows,
      this.damageByLine,
    );

    this.edit.receiptEdits = this.receiptPlan.receiptEdits;
    this.edit.addedReceipts = this.receiptPlan.addedReceipts;
    this.edit.deletedReceiptIds = this.receiptPlan.deletedReceiptIds;

    this.refreshDerived();
  }

  /**
   * How many PO lines this edit will REOPEN.
   *
   * A `completesLine: false` patch is the one thing this screen sends that reaches
   * past the log and changes what the PO screen says about a line for everyone, so
   * it is named above the Save button rather than left to be discovered afterwards —
   * the same treatment the container editor gives voiding and renumbering.
   *
   * Derived from the plan rather than from the rows, because the plan is what
   * actually goes on the wire: a line the user re-ticked before saving produces no
   * patch and so is not counted.
   */
  public get reopenedLineCount(): number {
    let count = 0;
    this.receiptPlan.receiptEdits.forEach((edits) => {
      if (edits.completesLine === false) { count++; }
    });
    return count;
  }

  /**
   * A PO whose received figures cannot be fully trusted — pre-tracking deliveries the
   * ledger cannot see, or tracked receipts sitting alongside them.
   *
   * Passed to the check-off, which words a closed line "Received (tracked)" rather
   * than "Closed" so the number reads as a TRACKED number rather than the whole
   * truth. On these POs some lines genuinely cannot be reconciled, and the honest
   * screen says so instead of pressing the user to certify otherwise.
   *
   * Shares `receivingNotice` with the mobile Items step, so the two cannot disagree
   * about which POs qualify.
   *
   * A FIELD, not a getter, and set exactly where {@link poValues} is. It is an
   * `@Input` to `app-items-entry`, so as a getter it re-ran `classifyReceivingScenario`
   * over every PO line on every change-detection pass — to answer a question that
   * cannot change between loads.
   */
  public isLegacyPo: boolean = false;

  /** Recomputes {@link isLegacyPo} from whatever PO has just been adopted (or from none). */
  private refreshLegacyPo(): void {
    this.isLegacyPo = !!this.poValues
      && receivingNotice(this.poValues.receivingStatus ?? null, this.poValues.lineItems ?? []) !== 'none';
  }

  /** Whether this log has a PO — gates the three PO-keyed parts of an edit (§4.3). */
  public get hasPo(): boolean {
    return !!(this.details?.log?.poNumber ?? '').trim();
  }

  /** Who the save will be attributed to. Blank is a `400`, so the UI blocks on it. */
  public get editedBy(): string {
    return this._entraService.UserEmail || '';
  }

  /** The caption shown for an item row — "BOX 2 of 3" active, plain type name if voided. */
  public itemCaptionFor(item: ReceivingLogEditItem): string {
    return editItemCaption(item);
  }

  /** The void-attribution line shown under a voided row. */
  public voidAttributionFor(item: ReceivingLogEditItem): string {
    return voidAttributionLine(item);
  }

  /** Formats an ISO date string as a local date; '' when absent/unparseable. */
  public formatDate(iso: string | null | undefined): string {
    if (!iso) { return ''; }
    const date = new Date(iso);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US');
  }

  /** Formats an ISO date-time string as a local date + time; '' when absent/unparseable. */
  public formatDateTime(iso: string | null | undefined): string {
    if (!iso) { return ''; }
    const date = new Date(iso);
    if (isNaN(date.getTime())) { return ''; }
    return `${date.toLocaleDateString('en-US')} ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  }

  // #endregion

  // #region Editing — the shared line-item check-off

  /**
   * The check-off is `LineItemCheckoffComponent`, the same control the delivery was
   * LOGGED with on mobile. This screen supplies the rows and takes them back; it
   * renders none of the interaction itself.
   *
   * ─────────────────────────────────────────────────────────────────────────────
   * THE §3.1 DEFENCE, AND WHY IT IS STRUCTURAL HERE
   * ─────────────────────────────────────────────────────────────────────────────
   *
   * The receipts grid this replaced bound inputs straight to a stored row's fields
   * and had to diff them back out by hand, one field at a time, because the obvious
   * implementation — bind an object, send the object — sends every field the row
   * rendered. Two of those do real damage: a `completesLine: false` the user never
   * touched RE-OPENS a closed PO line, and an `isDamaged: false` erases the damage
   * while its photos stay attached.
   *
   * The check-off cannot make that mistake, because nothing on screen is bound to a
   * receipt row at all. It edits PO LINES, and `planReceiptChanges` derives the
   * sparse patches from baseline-versus-edited. There is no path from a rendered
   * field to the wire that does not go through that diff.
   */

  /**
   * The rows the check-off renders: PO lines first, then this log's off-PO rows.
   *
   * ONE array rather than two, because that is what the control takes and what
   * `planReceiptChanges` reconciles — and because the off-PO rows' negative `line`
   * numbering is only unique against the PO lines when the two live in the same set.
   */
  public checkoffRows: CheckableLineItem[] = [];

  /**
   * The same rows exactly as seeded, never edited.
   *
   * `planReceiptChanges` diffs against THIS rather than against the stored ledger —
   * see its header for the two-logs-on-one-line case where target-versus-stored
   * silently rewrites a quantity nobody touched.
   */
  private _baselineRows: CheckableLineItem[] = [];

  /** Which rows the check-off should open on load — the ones this delivery touched. */
  public expandedRowIndices: number[] = [];

  /** PO lines this log has receipt rows against. Decides what stays editable. */
  private _linesWithLogReceipts = new Set<number>();

  /**
   * Which lines the check-off renders read-only.
   *
   * A line CLOSED before this delivery, that this log put nothing on, has nothing
   * here to correct — the same case the submit's Items step locks. But closed alone
   * is deliberately NOT the test: a line this log received against, including one it
   * closed itself, must stay editable or the arrival this screen exists to fix is
   * the one thing it cannot reach. That is also why the seed reports the PRIOR close
   * rather than po-data's, which already counts this log's own tap.
   *
   * Canceled lines are handled by the control itself, not here.
   *
   * An arrow property, not a method, so `this` survives being passed as a value.
   */
  public isLineReadOnlyForCheckoff = (row: CheckableLineItem): boolean => {
    if (row.isOffPo) { return false; }
    return !!row.isClosed && !this._linesWithLogReceipts.has(row.poLineSequence ?? -1);
  };

  /**
   * Why a locked line is locked, and — the part that matters — where to go to unlock
   * it.
   *
   * A line is read-only here only when it was closed BEFORE this delivery and this
   * log put nothing on it. That close came from somewhere else: another delivery
   * tapped it complete, or earlier receipts filled the ordered quantity. Either way
   * it is not this log's to take back, and nothing on this screen can do it — so the
   * note names the one action that can.
   *
   * Mobile keeps the shared default ("Fully received — no action needed."), which is
   * right there: on the submit there is no other log to go and edit, because the log
   * being written does not exist yet. The rest of the row — the pills, the greying,
   * the prior-receipts list — renders identically in both.
   */
  public readOnlyNoteForCheckoff = (_row: CheckableLineItem): string =>
    'Another log has already claimed this line, so it cannot be recorded here. To claim '
    + 'it on this log instead, open the log that claimed it and edit that one to release '
    + 'the line first.';

  /**
   * Takes the check-off's rows and re-derives the whole receipt plan.
   *
   * The plan REPLACES the three receipt fields rather than accumulating into them:
   * it is derived fresh from baseline-versus-edited every time, so it is already the
   * whole truth about what the check-off implies. Merging would leave behind patches
   * for a line the user has since put back.
   */
  public onCheckoffRowsChange(rows: CheckableLineItem[]): void {
    this.checkoffRows = rows;
    this.applyLineItemPlan();
  }

  /**
   * Puts the whole items edit back to what the log holds — the counterpart of
   * `resetContainers()`, so both editors offer the same escape hatch in the same
   * place on the card header.
   *
   * Rebuilt from `_baselineRows` rather than by re-fetching: the baseline is the
   * seed, and a refetch would also discard the PO read this screen paid a second
   * round trip for. Rows are COPIED out of it for the same reason `adoptSeed`
   * copies them — a shared reference would let the next tap move the baseline too,
   * and every line would then report itself unedited.
   *
   * The shortfall answer is cleared with the rest — see {@link clearPendingEdit},
   * which is where the reason all three of its fields have to go now lives.
   */
  public resetItemsEdit(): void {
    this.checkoffRows = this._baselineRows.map((row) => ({ ...row }));
    this.clearPendingEdit();
    this.applyLineItemPlan();
  }

  // #endregion

  // #region Editing — the shortfall clause

  /**
   * The fixed head of the shortfall clause, rendered as a LABEL rather than typed.
   *
   * Every stored clause starts with these words — `buildItemsString` composes it
   * that way at submit — so making the user retype them is both busywork and the
   * one part of the clause a typo would break: `splitPreservedNote` matches the
   * clause by this prefix, and "MISSING LINE ITMES 1" would stop being recognised
   * as a shortfall on the next edit and ride through as manual text instead.
   *
   * Always plural. `buildItemsString` picks singular for a one-line shortfall, and
   * both are read back (see {@link MISSING_HEAD_PATTERN}), but a fixed label cannot
   * agree with free text about how many lines it is going to name.
   */
  public readonly missingNoteHead: string = 'MISSING LINE ITEMS';

  /**
   * Matches either form of the head on a STORED clause, so an existing
   * `MISSING LINE ITEM 1 (X4)` prefills the field with `1 (X4)` rather than
   * with the whole clause repeated after the label.
   */
  private static readonly MISSING_HEAD_PATTERN = /^MISSING LINE ITEMS?\s*/i;

  /**
   * What the user types AFTER the label — the only part of the clause that is
   * theirs. Reads their version once they have one, and the stored clause's tail
   * until then.
   */
  public get missingNoteTail(): string {
    const clause = this.edit.missingNote ?? this.storedMissingClause;
    return clause.replace(ReceivingLogEditComponent.MISSING_HEAD_PATTERN, '');
  }

  /**
   * Whether the user has explicitly said nothing is owed.
   *
   * A separate flag rather than an inference from the empty field, because "I have
   * not filled this in" and "I am telling you there is nothing to fill in" are the
   * two states the gate below has to tell apart, and an empty string cannot say
   * which it is.
   */
  private _noMissingDeclared: boolean = false;

  /** Whether the note will carry a shortfall — the user's text, or the stored clause. */
  public get hasMissingText(): boolean {
    return (this.edit.missingNote ?? this.storedMissingClause).trim().length > 0;
  }

  /**
   * THE gate: the save is blocked until the user says what is still owed, or says
   * nothing is.
   *
   * ─────────────────────────────────────────────────────────────────────────────
   * ONE PREDICATE, TWO PROMPTS
   * ─────────────────────────────────────────────────────────────────────────────
   *
   * This was two blocking predicates — "the stored clause has gone stale" and "no
   * shortfall has been stated at all" — and they overlapped almost entirely: when a
   * clause was stored AND quantities moved, the second was satisfied by the stored
   * text alone and added nothing. Worse, only ONE of them was consulted by
   * `requestSave`, so the button's disabled state and the save path disagreed about
   * when the edit could go out.
   *
   * The two PROMPTS stay, because they say genuinely different things ("Is this
   * still right?" versus "What is still owed?") — {@link missingReviewNeeded} and
   * {@link hasMissingText} choose between them in the template. What they no longer
   * do is block independently.
   *
   * ─────────────────────────────────────────────────────────────────────────────
   * KEYED ON THIS LOG'S CLAIM, NOT ON THE PO'S STATUS
   * ─────────────────────────────────────────────────────────────────────────────
   *
   * The MISSING clause records what was short ON THIS DELIVERY. It is a per-log
   * fact, and the PO's overall status is not — so "the PO is complete" is NOT an
   * answer to "was this delivery short". A later delivery finishing the order says
   * nothing about whether this one arrived incomplete, and suppressing the question
   * because somebody else closed the PO is how a real shortfall goes unrecorded.
   *
   * The ONE thing that does answer it is this log claiming the completion itself: a
   * delivery that finished the order had nothing outstanding at the time, by
   * definition. That is the same fact — and the same field, `logCompletesPo` — that
   * `recomposeItemsNote` uses to drop the clause, so the two cannot disagree about
   * when a shortfall is possible.
   *
   * Everything else stops and asks. Typing satisfies it, and so does the explicit
   * "Nothing is still owed" button — but silence does not, because silence is what
   * produced the logs this editor exists to fix.
   *
   * Only when something is already being saved (`pendingChanges`): opening a log to
   * look at it must not demand a decision. And only in items mode — the container
   * editor has no view of what was ordered.
   *
   * A FIELD, recomputed by {@link refreshSaveGate}. As a getter it was reached five
   * or six times per change-detection pass, and each visit allocated an array of
   * patch objects (`buildReceiptUpdates`, through `receiptQuantitiesChanged`) and
   * re-sorted the quantity fingerprint.
   */
  public shortfallDecisionOutstanding: boolean = false;

  /** {@link shortfallDecisionOutstanding}, computed. Only {@link refreshSaveGate} calls this. */
  private computeShortfallDecisionOutstanding(): boolean {
    if (this.mode !== 'items' || !this.details?.hasStructuredRows) { return false; }
    if (!this.pendingChanges || this.logCompletesPoChecked) { return false; }
    return this.shortfallDecisionTriggered && !this.shortfallAnswered;
  }

  /**
   * Whether this edit is one the shortfall question applies to at all.
   *
   * Three separate reasons to ask, and an edit that hits none of them is silent:
   *
   *   - the quantities MOVED, so whatever the note says about what is owed was
   *     measured against numbers that no longer stand;
   *   - the note says nothing is owed at all, on a delivery that is not claiming to
   *     have completed the order — the silence this editor exists to break;
   *   - the PO is being REOPENED, which demands a fresh statement even on a log that
   *     already records a shortfall. Not because the stored clause is suspect — it
   *     may be a perfectly good record of what this delivery was short, and it stays
   *     true whoever completed the PO. The reason is that reopening is a NEW claim
   *     about the order: somebody has decided it is not finished after all, and the
   *     outstanding amount they are acting on is by definition not the one written
   *     down before it was called complete. Asking again is asking "what is owed
   *     NOW", which the old clause cannot answer even when it was right about the
   *     day it was written.
   *
   * So an edit that only ticks the PO status, on a log that already records a
   * shortfall, asks nothing.
   */
  private get shortfallDecisionTriggered(): boolean {
    return this.reopeningPo
      || receiptQuantitiesChanged(this.edit)
      || !this.hasMissingText;
  }

  /**
   * Whether the user has answered, about THESE numbers, in a way that stands.
   *
   * Three parts, and all three are load-bearing:
   *
   *   - the answer was given against the quantities as they now are. An answer that
   *     outlives the numbers it was about is the same silent staleness in a new
   *     place — see {@link _reviewedQuantities}.
   *   - it was given at all. `missingReviewDone` is set by every one of the three
   *     answers: typing, "Nothing is still owed", and "This still reads right".
   *   - and it still SAYS something. Pressing the button then emptying the field
   *     withdraws the declaration, and a confirmation of a clause that has since
   *     been deleted is not an answer either.
   *
   * On a REOPEN the last part demands the user's OWN text: confirming the stored
   * clause is not an answer to "what is owed now that the order isn't finished",
   * for the reason spelled out in {@link shortfallDecisionTriggered}. An explicit
   * "Nothing is still owed" does still pass, deliberately — it is a fresh statement
   * rather than a preserved one.
   */
  private get shortfallAnswered(): boolean {
    if (!this.missingReviewDone) { return false; }
    if (this._reviewedQuantities !== this.quantityEditFingerprint()) { return false; }
    if (this._noMissingDeclared) { return true; }
    return this.reopeningPo
      ? !!(this.edit.missingNote ?? '').trim()
      : this.hasMissingText;
  }

  /** Whether this edit is retracting a completion the PO already had stored. */
  public get reopeningPo(): boolean {
    return this.edit.completesPo === false;
  }

  /**
   * "Nothing is still owed" — the other way to answer.
   *
   * Writes `''` as well as setting the flag, so a log that DID record a shortfall has
   * it cleared: pressing this is the statement that the clause is wrong, and leaving
   * the old text behind while claiming nothing is owed is the contradiction the gate
   * exists to prevent. On a log with no clause `''` matches what is stored, so
   * `missingNoteEdited` stays false and nothing is sent.
   */
  public declareNoMissingItems(): void {
    this._noMissingDeclared = true;
    this.edit.missingNote = '';
    this.answerMissingReview();
    this.refreshDerived();
  }

  /**
   * Records the tail, upper-cased, and rebuilds the whole clause around it.
   *
   * Upper-cased as they type because the note is a stored, upper-cased string —
   * `buildItemsString` upper-cases the whole thing at submit — and a lower-case
   * clause spliced into it reads as a different system's text. Same treatment the
   * off-PO description field gets on the mobile add form.
   *
   * An EMPTY tail stores `''`, not `'MISSING LINE ITEMS'`: a label with nothing
   * after it is not a shortfall, and `''` is what drops the clause entirely (the
   * real edit "nothing is owed after all"). The head is only ever attached to text.
   *
   * Typing here is BOTH the edit and the answer to the stale-shortfall prompt —
   * putting words in this field IS a decision about what is owed.
   */
  public set missingNoteTail(value: string) {
    // Upper-cased but NOT trimmed. Trimming on every keystroke eats the space the
    // moment it is typed, so a second word can never be started — the field would
    // accept "MISSING" and silently refuse "MISSING LINE". Emptiness is tested
    // against a trimmed copy instead, and `recomposeItemsNote` trims the override
    // before it reaches the wire, so nothing downstream sees the stray space.
    const tail = String(value ?? '').toUpperCase();
    this.edit.missingNote = tail.trim() ? `${this.missingNoteHead} ${tail}` : '';
    // Typing supersedes the "nothing is owed" declaration, and CLEARING the field
    // withdraws it — otherwise emptying the box after pressing that button would
    // sail past the gate on a decision the user has just taken back.
    this._noMissingDeclared = false;
    this.answerMissingReview();
    this.refreshDerived();
  }

  /**
   * "This still reads right" — the other way to answer the prompt.
   *
   * Leaves `edit.missingNote` as `null` on purpose: confirming is a statement that
   * the STORED clause is still correct, so the stored clause is what should be
   * preserved, and nothing is written that merely echoes it.
   */
  public confirmMissingNote(): void {
    this.answerMissingReview();
    this.refreshDerived();
  }

  /** Records that the prompt was answered, and against WHICH quantities. */
  private answerMissingReview(): void {
    this.missingReviewDone = true;
    this._reviewedQuantities = this.quantityEditFingerprint();
  }

  /**
   * A stable string of every quantity this edit moves. Only the quantity-bearing
   * parts, so a notes or status edit never re-asks the shortfall question.
   */
  private quantityEditFingerprint(): string {
    const updated = [...this.edit.receiptEdits.entries()]
      .map(([id, edits]) => `${id}=${edits.quantityReceived ?? ''}`)
      .sort()
      .join('|');
    const added = (this.edit.addedReceipts ?? []).map((row) => String(row.quantityReceived ?? '')).join(',');
    const deleted = [...(this.edit.deletedReceiptIds ?? [])].sort((a, b) => a - b).join(',');
    return `${updated}#${added}#${deleted}`;
  }

  /**
   * Whether the save must stop and make the user decide about the shortfall.
   *
   * The stored `MISSING …` clause was never derived from data — it came from the
   * user's own missing-page selections at submit, which were never persisted — so
   * nothing here can recompute it when the received quantities move. Change a
   * quantity 6→2 and a preserved `MISSING LINE ITEM 1 (X4)` silently asserts a
   * shortfall of 4 when 8 are really owed. Turning that silent stale number into a
   * decision is the whole fix.
   *
   * A MESSAGE SELECTOR, not a blocker. It decides which of the two prompts the
   * template shows — "Is this still right?" against "What is still owed?" — while
   * {@link shortfallDecisionOutstanding} decides whether the save can go out at
   * all. The two used to be the same predicate, which is how the screen ended up
   * with two gates that disagreed.
   */
  public get missingReviewNeeded(): boolean {
    // Nothing to review once this log claims the completion: the clause is being
    // dropped either way (`recomposeItemsNote`), so demanding the user confirm text
    // that will not be sent blocks the save on a decision with no effect.
    if (this.logCompletesPoChecked) { return false; }
    return !!this.details && missingClauseNeedsReview(this.details, this.edit);
  }

  /**
   * Whether the outstanding decision is one the user answers by WRITING something
   * — nothing is recorded as owed, or the PO is being reopened and the stored
   * clause cannot speak for it.
   *
   * The other case (a stored clause that has gone stale under a quantity edit) is
   * answered by confirming it instead, so it gets the confirm button and the other
   * wording. Purely a which-control/which-sentence selector; both cases block
   * through the one predicate.
   */
  public get shortfallNeedsStating(): boolean {
    return this.shortfallDecisionOutstanding && (this.reopeningPo || !this.hasMissingText);
  }

  /** The outstanding decision is one the user answers by confirming the stored clause still reads right. */
  public get shortfallNeedsConfirming(): boolean {
    return this.shortfallDecisionOutstanding && !this.shortfallNeedsStating;
  }

  // #endregion

  // #region Editing — containers

  /**
   * The container chips moved. Works out what the new counts mean and folds it
   * into the pending edit.
   *
   * The chips are the ONLY container control. Tapping UP is a plain addition —
   * the backend mints the item rows and their ids (§3.6). Tapping DOWN takes the
   * highest-numbered containers of that type off, because "the last one came off"
   * is the one choice that needs no explanation.
   *
   * What the save bar states before committing is the COUNT coming off and whether
   * the survivors are renumbered — not which specific ids. Naming them was the
   * original intent and there is an argument for it under §3.3; it is not what the
   * screen does today, and nothing here should claim otherwise.
   *
   * There is deliberately NO per-container picker. Every void renumbers the
   * survivors of its type and forces a reprint (§3.4/§6.2.3), and applying fresh
   * stickers by hand does not preserve which id was on which box — so choosing a
   * specific container buys an accuracy the next reprint immediately discards, at
   * the cost of a second control that has to be kept in agreement with the first.
   * What IS worth recording is the reason, which applies to the whole void.
   */
  public onContainerRowsChange(rows: ContainerRow[]): void {
    this.containerTargetRows = rows ?? [];
    this.containerPlan = planContainerChanges(this.activeItems, this.containerTargetRows);
    this.edit.addedContainers = this.containerPlan.added.map((item) => ({ ...item }));
    this.edit.voidItemIds = [...this.containerPlan.proposedVoidItemIds];
    this.refreshDerived();
  }

  /** Puts the chips back to what the log actually holds and drops the pending edit. */
  public resetContainers(): void {
    this.containerTargetRows = containerRowsFromItems(this.activeItems);
    this.clearPendingEdit();
    this.refreshDerived();
  }

  /** How many containers this edit is taking off the log. */
  public get voidCount(): number {
    return this.edit.voidItemIds.length;
  }

  /** Whether the chips have moved off what the log actually holds. */
  public get containersChanged(): boolean {
    return this.containerPlan.added.length > 0 || this.voidCount > 0;
  }

  /**
   * Whether an addition lands on a type the log ALREADY has — the only kind that
   * invalidates existing labels (§3.4). Adding a type the log has never had
   * renumbers nothing, and warning about it would train the user straight past
   * the warning that matters.
   */
  public get addsToExistingContainerType(): boolean {
    return addsToExistingType(this.activeItems, this.containerPlan);
  }

  /**
   * Whether the VOID half of this edit renumbers labels — i.e. some type loses a
   * container and keeps at least one.
   *
   * Deliberately narrower than {@link renumbersLabels}, which is true if EITHER half
   * renumbers. Voiding an entire type renumbers nothing (there are no survivors to
   * renumber), so a void note driven off the broader flag would claim a consequence
   * this void does not have whenever the same edit also added to an existing type.
   *
   * Computed from a plan carrying only the voids, so the add half cannot leak in.
   */
  public get voidRenumbersSurvivors(): boolean {
    return planRenumbersLabels(this.activeItems, {
      added: [],
      proposedVoidItemIds: this.containerPlan.proposedVoidItemIds,
    });
  }

  /** Whether the PO-complete box is ticked: the pending decision, else what is stored. */
  public get poCompleteChecked(): boolean {
    return this.edit.completesPo ?? this.storedPoComplete;
  }

  /** The stored whole-PO completion. Absence of a status row means not complete. */
  public get storedPoComplete(): boolean {
    return this.details?.poStatus?.isComplete ?? false;
  }

  /**
   * "who marked this PO complete, and when" — or null when nobody has.
   *
   * Composed here rather than in the template so the row can be gated on the whole
   * sentence existing. `MM.ReceivingLogPoStatus` holds no row at all until someone
   * completes a PO, and it can also hold one that says NOT complete (an earlier
   * reopen), which has a `completedBy` recorded against a decision that no longer
   * stands — attributing that as "marked by" would be false.
   */
  public get poCompletedAttribution(): string | null {
    const status = this.details?.poStatus;
    if (!status?.isComplete || !status.completedBy) { return null; }
    const on = status.completedDate ? ` on ${this.formatDate(status.completedDate)}` : '';
    return `${status.completedBy}${on}`;
  }

  /**
   * Ticks or unticks whole-PO completion.
   *
   * Note what happens when the box is put BACK to where it started:
   * `completesPo` returns to `null`, which omits it from the request entirely and
   * leaves `MM.ReceivingLogPoStatus` untouched. That is the difference between
   * "the user decided the PO is open" and "the user never touched this", and it
   * matters — the second must not write a row, or every save from this screen
   * would stamp `completedBy`/`completedDate` on a PO nobody made a decision
   * about.
   *
   * `MM.ReceivingLogPoStatus` is ONE ROW PER PO, keyed on the PO number — there
   * is no per-log completion — so ticking this changes the whole PO from one
   * log's screen, which the template says out loud (§1.2).
   */
  public setPoComplete(checked: boolean): void {
    this.edit.completesPo = checked === this.storedPoComplete ? null : checked;
    this.refreshDerived();
  }

  /**
   * ─────────────────────────────────────────────────────────────────────────────
   * TWO QUESTIONS, NOT ONE
   * ─────────────────────────────────────────────────────────────────────────────
   *
   * `MM.ReceivingLogPoStatus` is one row per PO and records `completedBy` and
   * `completedDate`, but NOT WHICH LOG completed it. The note's `PO COMPLETE` clause
   * is therefore the only per-log record there is — a log whose note carries it is
   * the delivery that claimed the PO was finished.
   *
   * So the screen asks separately:
   *
   *   1. did THIS delivery complete the PO?  → the note clause
   *   2. is the PO complete?                 → the status row
   *
   * Ticking (1) implies (2), because a delivery cannot complete a PO that is not
   * complete. Unticking (1) leaves (2) ALONE: another delivery may have finished the
   * order, and since no row says which one, reopening the PO here would discard a
   * fact nothing can reconstruct. Reopening is its own separate tick.
   */

  /** Whether this log claims the completion: the pending decision, else what the stored note says. */
  public get logCompletesPoChecked(): boolean {
    return this.edit.logCompletesPo ?? this.storedLogCompletesPo;
  }

  /**
   * Whether the STORED NOTE already claims this log completed the PO.
   *
   * Read from the note rather than from `poStatus`, which cannot answer it — that is
   * the whole reason these are two fields.
   *
   * A FIELD, set once per load in {@link applyDetails}. It is reached from four
   * template positions through `logCompletesPoChecked`, and as a getter each one
   * re-split and re-matched the stored note on every change-detection pass, to
   * answer a question about text that cannot change until the log is read again.
   */
  public storedLogCompletesPo: boolean = false;

  /**
   * Records whether this delivery completed the PO.
   *
   * Ticking it also completes the PO when it is not already — the claim would
   * otherwise contradict the status. Unticking it deliberately does NOT reopen the
   * PO; that is offered separately, and only once there is something to reopen.
   */
  public setLogCompletesPo(checked: boolean): void {
    this.edit.logCompletesPo = checked === this.storedLogCompletesPo ? null : checked;
    // Moving this log's own claim re-opens the question of whose delivery closed the
    // PO, so a previous answer to it stops standing. Without this, dropping the
    // claim, answering, re-ticking it and dropping it again would sail past a
    // question the user last answered about a different arrangement of the facts.
    this._poCompletionAnswered = false;
    if (checked && !this.poCompleteChecked) { this.setPoComplete(true); }

    // Unticking WITHDRAWS a completion this same edit implied — but never one that
    // was already stored.
    //
    // Ticking the claim completes the PO as a consequence, so leaving that behind
    // when the claim is taken back strands a decision nobody made. On a PO that was
    // never complete it also produced genuinely broken-looking behaviour: tick, then
    // untick, and `completesPo` stayed `true` while the claim went to `null`, which
    // is exactly the state `canReopenPo` offers the reopen tick for. Pressing that
    // tick called `setPoComplete(false)`, which on a PO whose stored status is
    // already `false` resolves to `null` — so the box unticked itself out of
    // existence rather than reopening anything.
    //
    // The stored case is deliberately untouched: another delivery may have finished
    // the order and no row says which, so dropping this log's claim is not evidence
    // the PO is open. That is what the separate reopen tick is for.
    if (!checked && !this.storedPoComplete && this.edit.completesPo === true) {
      this.setPoComplete(false);
    }

    this.refreshDerived();
  }

  /**
   * Whether to ask WHICH delivery completed this PO.
   *
   * Only when this log is not claiming the completion of a PO that is still marked
   * complete — the one case where the two answers can legitimately diverge and the
   * user may have meant both.
   *
   * The `completesPo === false` arm is what keeps the question ON SCREEN after it is
   * answered. Without it the condition is written in terms of the value the answer
   * CHANGES: reopening makes `poCompleteChecked` false, which makes this false, which
   * removes the control the user just used — the decision is still pending and still
   * about to be sent, but there is nothing left saying so and no way to take it back
   * except closing the modal.
   */
  public get canReopenPo(): boolean {
    if (this.logCompletesPoChecked) { return false; }
    return this.poCompleteChecked || this.edit.completesPo === false;
  }

  /**
   * Whether the user has answered "which delivery completed this PO".
   *
   * ─────────────────────────────────────────────────────────────────────────────
   * WHY A FLAG RATHER THAN A READ OF `completesPo`
   * ─────────────────────────────────────────────────────────────────────────────
   *
   * The two answers are "another delivery completed it" and "no delivery did". The
   * FIRST one writes nothing: the PO is already marked complete and saying somebody
   * else did it leaves `MM.ReceivingLogPoStatus` exactly as it stands, so
   * `completesPo` goes back to `null` — which is the same value it has when the user
   * has said nothing at all.
   *
   * That is precisely the distinction this question exists to force. It was a
   * checkbox whose unchecked state meant "somebody else completed it", so an edit
   * could go out on that reading without anyone ever making it; three states are
   * needed to tell an answer from a silence, and a boolean field is the third.
   */
  private _poCompletionAnswered: boolean = false;

  /**
   * Which option the radio shows selected, or `null` while it is unanswered.
   *
   * Read from the flag first: `completesPo === null` means "answered, and the answer
   * writes nothing" only once {@link _poCompletionAnswered} says so.
   */
  public get poCompletionAnswer(): 'anotherDelivery' | 'noDelivery' | null {
    if (!this._poCompletionAnswered) { return null; }
    return this.edit.completesPo === false ? 'noDelivery' : 'anotherDelivery';
  }

  /**
   * "A separate logged delivery completed this PO."
   *
   * Leaves the status row ALONE, which is what `setPoComplete` resolves this to: the
   * PO is already stored complete, so asserting it again is a no-op and nothing goes
   * on the wire. The answer is still recorded locally, because the point of asking is
   * that the save carries a decision rather than a default.
   */
  public answerAnotherDeliveryCompletedPo(): void {
    this._poCompletionAnswered = true;
    this.setPoComplete(true);
  }

  /**
   * "This PO has NOT been completed by any other logged delivery."
   *
   * The one path that sends `completesPo: false`, reopening the whole PO — one row
   * per PO, so this changes what every screen says about the order, not just this
   * delivery. It also makes {@link reopeningPo} true, which is what puts the
   * shortfall question into "what is owed NOW" mode.
   */
  public answerNoDeliveryCompletedPo(): void {
    this._poCompletionAnswered = true;
    this.setPoComplete(false);
  }

  /**
   * True while the question is on screen, this edit has something to send, and the
   * user has not answered — the save is blocked until they do.
   *
   * Gated on `pendingChanges` for the same reason the shortfall gate is: opening a
   * log to look at it must not demand a decision. A REQUIRED answer, rather than the
   * checkbox's silent default, because the two readings of that default are
   * "somebody else finished this order" and "I did not think about it", and the
   * first is a claim about the PO that outlives this screen.
   */
  private computeReopenDecisionOutstanding(): boolean {
    if (this.mode !== 'items' || !this.hasPo) { return false; }
    if (!this.pendingChanges) { return false; }
    return this.canReopenPo && !this._poCompletionAnswered;
  }

  /** {@link computeReopenDecisionOutstanding}, held as a field — see {@link refreshSaveGate}. */
  public reopenDecisionOutstanding: boolean = false;

  // #endregion

  // #region Saving

  /** Recomputes every derived preview and the validation list. Called after any edit. */
  public refreshDerived(): void {
    if (!this.details) { return; }

    this.pendingChanges = hasPendingChanges(this.details, this.edit);
    // Only an edit that actually moves an EXISTING label's caption earns the
    // pre-save confirmation. A brand-new container type renumbers nothing, and
    // confirming it anyway is how a user learns to click through the one that
    // matters. `renumberedItems` on the response stays the authoritative list.
    //
    // ITEMS mode never renumbers anything. This was a `mode === 'quantity' ? … :
    // willRenumberLabels(edit)` ternary whose items arm read only the container
    // fields — `voidItemIds` and `addedContainers` — which are permanently empty
    // there, because the container editor is the only thing that writes them and it
    // is quantity-mode only. So the arm was a constant `false`, and the ternary
    // collapses to the call that does the work.
    this.renumbersLabels = this.mode === 'quantity'
      && planRenumbersLabels(this.activeItems, this.containerPlan);
    // The plan's refusals are BLOCKING problems, not notes. Each one is an edit the
    // user made that this endpoint cannot express, and the only alternative to
    // stopping on it is a save that reports success while keeping the old value.
    this.validationProblems = [
      ...validateLogEditState(this.details, this.edit, this.editedBy),
      ...this.receiptPlan.unsupported,
    ];
    this.quantityAfterEdit = previewQuantityAfterEdit(
      this.activeItems,
      this.edit.voidItemIds,
      this.edit.addedContainers,
    );

    // Building the request is the only honest way to preview the note: it is the
    // same call the save makes, so the preview cannot drift from what is sent.
    const request = buildLogEditRequest(this.details, this.edit, this.editedBy);
    this.notePreview = request.items ?? null;

    // A pending confirmation is void once the edit changes under it.
    if (this.saveState === 'confirming') { this.saveState = 'idle'; }

    // LAST, because it reads what everything above just wrote.
    this.refreshSaveGate();
  }

  /**
   * Recomputes the three save-bar answers. Pure reads — nothing here mutates the
   * edit, so it is safe to call from anywhere, including from inside a save.
   *
   * Separate from {@link refreshDerived} because the gate depends on TWO things
   * beyond the edit — `saveState` and `staleAfterSave` — and `refreshDerived` cannot
   * be called when either of those moves: it clears a pending
   * `saveState === 'confirming'`, so calling it just after arming the confirmation
   * would disarm it immediately. Every save-state transition goes through
   * {@link setSaveState}, which calls this instead, and the one place
   * `staleAfterSave` is set calls it directly.
   */
  private refreshSaveGate(): void {
    this.shortfallDecisionOutstanding = this.computeShortfallDecisionOutstanding();
    this.reopenDecisionOutstanding = this.computeReopenDecisionOutstanding();
    this.canSave = this.computeCanSave();
    // AFTER canSave — its first line reads it.
    this.whySaveDisabled = this.computeWhySaveDisabled();
  }

  /**
   * Moves the save state and re-answers the save bar with it.
   *
   * One method rather than a bare assignment at each of the eight transitions,
   * because {@link canSave} and {@link whySaveDisabled} are now fields: an
   * assignment that forgets to re-derive them leaves the button reading the previous
   * state, which is the same class of staleness {@link clearPendingEdit} exists to
   * prevent.
   */
  private setSaveState(next: SaveState): void {
    this.saveState = next;
    this.refreshSaveGate();
  }

  /**
   * The Save button. Validates, then — when this edit will renumber labels —
   * stops on a confirmation step BEFORE sending rather than after (§3.4): adding
   * one box to a log that had two turns `BOX 1 of 2` into `BOX 1 of 3`, so every
   * existing box label is wrong, not just the new one.
   *
   * The shortfall decision is checked first and separately, because it is not a
   * warning to click past: it is a value that goes on the wire either way.
   */
  public requestSave(): void {
    if (!this.details || this.staleAfterSave) { return; }

    // Read the confirmation BEFORE re-validating, because `refreshDerived` clears it.
    //
    // That clearing is correct where it was written for — a pending confirmation is
    // void once the EDIT changes under it, and every edit handler calls
    // `refreshDerived`. But this method calls it too, purely to re-validate, and the
    // check below then read the state it had just reset: pressing "Save anyway" put
    // the screen straight back into `confirming` instead of saving. The save could
    // never go out at all on the one path that reaches this — an edit that renumbers
    // labels, i.e. adding to a container type the log already has, or voiding one
    // while others of that type survive. Adding a BRAND-NEW type renumbers nothing,
    // takes no confirmation, and saved fine, which is why it went unnoticed.
    const alreadyConfirmed = this.saveState === 'confirming';

    this.refreshDerived();
    if (this.validationProblems.length > 0 || !this.pendingChanges) { return; }
    // The SAME predicates the button is disabled on. The shortfall gate used to be
    // only its stale-clause half here, so a save the button correctly refused could
    // still be sent by any other route into this method.
    if (this.reopenDecisionOutstanding) { return; }
    if (this.shortfallDecisionOutstanding) { return; }

    if (this.renumbersLabels && !alreadyConfirmed) {
      this.setSaveState('confirming');
      return;
    }
    this.save();
  }

  public cancelConfirm(): void {
    this.setSaveState('idle');
  }

  /**
   * Everything that has to be true before the `PUT` can go out.
   *
   * A FIELD, recomputed by {@link refreshSaveGate}, because the template asks for it
   * on every change-detection pass and answering used to mean rebuilding the receipt
   * patches twice over through the shortfall gate.
   */
  public canSave: boolean = false;

  /** {@link canSave}, computed. Only {@link refreshSaveGate} calls this. */
  private computeCanSave(): boolean {
    return (
      this.pendingChanges &&
      this.validationProblems.length === 0 &&
      !this.reopenDecisionOutstanding &&
      !this.shortfallDecisionOutstanding &&
      this.saveState !== 'saving' &&
      !this.staleAfterSave
    );
  }

  /**
   * Why the save button is disabled, in the user's terms — or null when it isn't.
   *
   * A greyed button with no stated reason is indistinguishable from a broken one.
   * Four of the six things {@link canSave} requires get a sentence here, each saying
   * what to do next rather than what is wrong; the other two are deliberately silent
   * — `saving` speaks through the spinner, and `staleAfterSave` through the outcome
   * block below (see the early returns).
   *
   * The shortfall gate is one condition with two wordings, chosen by the same
   * selector the two prompts use, so the reason under the button names the control
   * the user is actually being sent to.
   *
   * A FIELD, recomputed by {@link refreshSaveGate} — and computed AFTER
   * {@link canSave}, which its first line reads.
   */
  public whySaveDisabled: string | null = null;

  /** {@link whySaveDisabled}, computed. Only {@link refreshSaveGate} calls this. */
  private computeWhySaveDisabled(): string | null {
    if (this.canSave || this.saveState === 'saving') { return null; }
    // Deliberately SILENT once a save has landed. The outcome now renders directly
    // beneath this line, opening with its own "Saved." — so a disabled-reason saying
    // the same word first is the screen telling the user twice, in the wrong order.
    // The "close and reopen" instruction lives at the end of that block instead,
    // after the result it applies to.
    if (this.staleAfterSave) { return null; }
    if (!this.pendingChanges) {
      return this.mode === 'quantity'
        ? 'Nothing has changed yet — tap a container to add one, or use its “−” to take one off.'
        : 'Nothing has changed yet.';
    }
    if (this.validationProblems.length > 0) { return 'Fix the problems above before saving.'; }
    // Named before the shortfall, matching the order the two questions appear on
    // screen: the PO-status card sits above the check-off and the "Still owed" card.
    if (this.reopenDecisionOutstanding) {
      return 'Say whether another delivery completed this PO before saving.';
    }
    if (this.shortfallNeedsStating) {
      return 'Say what this delivery is still owed — or confirm nothing is.';
    }
    if (this.shortfallNeedsConfirming) { return 'Confirm what is still owed before saving.'; }
    return null;
  }

  /** Sends the `PUT`. The outcome is handled by {@link applySaveResponse}/{@link applyRefusal}. */
  public save(): void {
    if (!this.details || this.logId == null) { return; }

    const request = buildLogEditRequest(this.details, this.edit, this.editedBy);
    this.setSaveState('saving');
    this.saveResponse = null;
    this.savedView = null;
    this.refusal = null;
    this.conflictMessage = null;
    // Captured BEFORE the round-trip so §6.1's before/after can be shown: the
    // response's quantityDisplay is the after, and after applyDetails runs there is
    // no copy of the before left anywhere.
    this._quantityBeforeSave = this.quantityPreviewText;

    this._saveSub?.unsubscribe();
    this._saveSub = this._api.putReceivingLogEdit(this.logId, request).subscribe({
      next: (response) => this.applySaveResponse(response),
      error: (err) => {
        console.error('Error saving receiving log edit:', err);
        this.applyRefusal(err);
      },
    });
  }

  // #endregion

  // #region Outcome — §6

  /**
   * Reads a `200` (§6).
   *
   * A `200` here ALWAYS means the transaction committed — `outcome` is never
   * `"Failed"` on this endpoint, because a refusal is a `400` and a failure a
   * `500`. So this method never presents a save as having not happened; the worst
   * it reports is `CompletedWithWarnings`, which means the rows landed and
   * something after the commit didn't.
   *
   * The order of the four things it does matters:
   *
   * 1. The quantity preview is REPLACED with the response's `quantityDisplay` —
   *    that is what is stored, and the local `quantityAfterEdit` was only ever a
   *    guess (§6.2.1). The before/after is kept for §6.1's legacy case, where
   *    deriving the quantity overwrites the only record of what arrived.
   * 2. The renumber warning is built as a BLOCK, not a toast (§6.2.3).
   * 3. The result the parent re-baselines from is captured — from the RESPONSE,
   *    never from a preview.
   * 4. Only genuinely-failed parts are toasted. `statusCode: "0"` is NOT ATTEMPTED
   *    (§6): `poCompletionStatus` is "0" unless a `completesPo` was sent, and it
   *    carries `isSuccess: false`. Toasting that would report a half-failure on
   *    most saves.
   */
  private applySaveResponse(response: UpdateReceivingLogResponse): void {
    this.saveResponse = response;
    const view = interpretSaveResponse(response);
    this.savedView = view;

    // 1. What is STORED replaces the preview.
    this.quantityNotice = quantityChangeNotice(this._quantityBeforeSave, view.quantityDisplay);
    this.quantityPreviewText = view.quantityDisplay;

    // 2. The explicit already-in-the-warehouse statement.
    this.renumberWarning = renumberedLabelWarning(response, this.printWarning === 'printed');

    // 3. What the parent needs to stop diffing against a row that no longer
    //    exists. Both strings come off the response — the only place the STORED
    //    values are, and the only place `renumberedItems` is at all.
    this._savedResult = {
      quantity: view.quantityDisplay,
      items: view.storedNote,
      summaryLines: view.summaryLines,
      renumberWarning: this.renumberWarning,
      renumberedCaptions: view.renumberedCaptions,
    };

    // The pending edit has landed; nothing is left to send. The rows still on
    // screen are now HISTORICAL — the ids, the numbering and the receipt set all
    // moved server-side — so a second edit built on them would assert a stale
    // `expected` and earn a 409 (§5). Saving is blocked until the log is reloaded.
    //
    // Every copy of the edit goes, not just `edit` itself — see
    // {@link clearPendingEdit}, which records what a surviving `containerPlan` did
    // to this screen when it was cleared here by hand and one field was missed.
    this.clearPendingEdit();
    this.refreshDerived();
    // `staleAfterSave` is a save-gate input that `refreshDerived` knows nothing
    // about, so the gate is re-answered after it moves, not before.
    this.staleAfterSave = true;
    this.setSaveState('saved');

    // 4. Toasts. The existing plumbing, unchanged — handleOperationStatus renders a
    // non-success status as "{label} failed: {message}" — but it is only ever given
    // parts that were attempted AND failed.
    for (const part of partStatusFailures(response)) {
      void this._toastService.handleOperationStatus(part.status, part.label);
    }

    // §6.1's invariant broke: the response's two halves describe different logs.
    // Only meaningful when this edit actually touched item rows.
    //
    // Raised BEFORE the auto-close branch below, not after it. This is already a
    // toast by design, so it survives the modal closing — but it is reachable on an
    // otherwise-clean save (it is independent of `outcome` and `incompleteParts`),
    // so leaving it after an early return is how it would silently stop firing on
    // exactly the saves that close themselves.
    if (touchedItemRows(response) && view.quantityDisagreesWithItems) {
      void this._toastService.showWarningMessage(
        'The saved quantity does not match the saved item rows — re-check the labels before printing.',
        6000,
      );
    }

    // 5. Report, and get out of the way.
    //
    // On a clean save the toast IS the report and the screen closes itself: the rows
    // behind it are now historical, saving again is blocked, and the entry is
    // re-baselined from the response the moment this closes — so staying open only
    // buys the user a second dismissal to perform. The toast therefore has to name
    // what MOVED (see `saveToastMessage`), because it is the only report they get.
    //
    // It does NOT close when something has to be read — a renumber warning above
    // all, which §6.2.3 requires be a block rather than a toast because it means
    // stickers on boxes in the warehouse are wrong and this response is the only
    // place the list of which ones exists. Closing over it would destroy that list
    // to save a click.
    if (canCloseAfterSave(view, this.renumberWarning !== null)) {
      void this._toastService.showSuccessMessage(saveToastMessage(view, this.mode));
      this.close();
      return;
    }

    if (view.savedCleanly) {
      void this._toastService.showSuccessMessage(saveToastMessage(view, this.mode));
    } else {
      void this._toastService.showWarningMessage('Saved, with warnings — see the notes on this page.', 6000);
    }
  }

  /**
   * Reads a refusal (§4, §5.2) and answers it.
   *
   * The `409` branch is the one with teeth. Its body is a `{ message, detail }`
   * WRAPPER, not a bare details payload: the message names which ids appeared or
   * disappeared and is shown verbatim, and `detail` is byte-identical to the GET so
   * it is fed straight back through {@link applyDetails}, which resets the pending
   * edit. That reset is the point — the user REDOES the edit against what is
   * actually there.
   *
   * What this must never do, and does not: re-send the same body, or merge `detail`
   * into the pending edit behind the user's back. The request is not idempotent —
   * it mints item positions and inserts receipt rows — so a replay that half-ran
   * doubles them. There is no retry button on the conflict block for the same
   * reason; the only way forward is to redo the edit.
   *
   * §5.3's second route to a `409` (losing a lock race rather than being stale) is
   * not told apart, because the handling is identical and only the message differs.
   */
  private applyRefusal(err: unknown): void {
    const refusal = classifySaveRefusal(err);
    this.refusal = refusal;

    if (refusal.kind === 'conflict' && refusal.conflictDetail) {
      // Re-render from the fresh state. applyDetails resets saveState/pending edit,
      // so the message and the state are set AFTER it, not before.
      this.applyDetails(refusal.conflictDetail);
      this.refusal = refusal;
      this.conflictMessage = refusal.message;
      this.setSaveState('conflict');
      void this._toastService.showWarningMessage('This log changed — the edit was not applied.', 6000);
      return;
    }

    if (refusal.kind === 'conflict') {
      // A 409 whose wrapper carried no detail: there is nothing to re-render from,
      // so send the user back through a full reload rather than leave them editing
      // state the server has already rejected.
      this.conflictMessage = refusal.message;
      this.setSaveState('conflict');
      return;
    }

    if (refusal.kind === 'notFound') {
      // The log was deleted or archived under the user. Nothing was written and
      // there is nothing left to reload, so the screen stops being an edit screen.
      this.state = 'notFound';
      this.setSaveState('refused');
      return;
    }

    this.setSaveState('refused');
    void this._toastService.showWarningMessage(refusal.message, 6000);
  }

  /** Dismisses the conflict banner. The screen already shows the reloaded state. */
  public acknowledgeConflict(): void {
    if (this.saveState === 'conflict') { this.setSaveState('idle'); }
    this.conflictMessage = null;
  }

  /** Reloads after a conflict — the same full reload the "Try again" button runs. */
  public reloadAfterConflict(): void {
    this.loadDetails();
  }

  // #endregion
}
